/**
 * Fetches city and state for a given 6-digit Indian pincode.
 *
 * Primary:  https://api.postalpincode.in/pincode/:pin
 * Fallback: https://api.zippopotam.us/in/:pin
 *
 * The primary API has an intermittent SSL cert issue on some networks.
 * The fallback is used automatically when the primary fails.
 */

const parsePrimary = async (pincode) => {
  const response = await fetch(
    `https://api.postalpincode.in/pincode/${pincode}`,
  );
  const data = await response.json();
  const result = data?.[0];

  if (!result || result.Status === "Error" || result.Status === "404") {
    return null;
  }

  const postOffice = result.PostOffice?.[0];
  if (!postOffice) return null;

  return {
    city: postOffice.District || postOffice.Block || "",
    state: postOffice.State || "",
  };
};

const parseFallback = async (pincode) => {
  const response = await fetch(`https://api.zippopotam.us/in/${pincode}`);
  if (!response.ok) return null;
  const data = await response.json();

  const place = data?.places?.[0];
  if (!place) return null;

  return {
    city: place["place name"] || "",
    state: place["state"] || "",
  };
};

export const fetchPincodeDetailsAction = async (pincode) => {
  // Try primary API
  try {
    const result = await parsePrimary(pincode);
    if (result) return { success: true, ...result };
  } catch {
    // Primary failed (SSL error, network issue, etc.) — try fallback
  }

  // Try fallback API
  try {
    const result = await parseFallback(pincode);
    if (result) return { success: true, ...result };
  } catch {
    // Both failed
  }

  return {
    success: false,
    error:
      "Failed to fetch pincode details. Please enter city and state manually.",
  };
};
